import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ExternalLink, Globe, LogOut, Menu } from 'lucide-react';
import { languageOptions } from '../../i18n';

export const Navbar = ({ user, onLogout, onMenuClick, onHomeClick }) => {
  const { t, i18n } = useTranslation();
  const [showProfile, setShowProfile] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = user?.full_name
    ? user.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email?.[0]?.toUpperCase() || 'U');

  const roleLabel = {
    admin: 'Admin',
    content_admin: 'Content Admin',
    partner_admin: 'Partner Admin',
    training_partner: 'Trainer',
    ms_stakeholder: 'MS Stakeholder',
  }[user?.role] || user?.role;

  return (
    <nav className="bg-levelup-dark shadow-lg sticky top-0 z-[60] w-full">
      <div className="w-full px-4 lg:px-8">
        <div className="flex justify-between items-center h-12">
          {/* Left: Brand */}
          <div className="flex items-center space-x-3">
            <button onClick={onMenuClick} className="lg:hidden p-2 rounded-lg hover:bg-levelup-teal text-white">
              <Menu size={22} />
            </button>
            <button
              onClick={onHomeClick}
              className="flex items-center space-x-3 cursor-pointer hover:opacity-90 transition-opacity"
              data-testid="header-home-link"
            >
              <img src="/technofocus-white.png" alt="Technofocus" className="h-5 brightness-0 invert" />
              <span className="text-levelup-accent font-bold text-base">|</span>
              <img src="/logo-levelup.png" alt="LevelUp" className="h-5" />
              <span className="text-levelup-accent font-bold text-base hidden md:inline">|</span>
              <img src="/ms-levelup-logo.svg" alt="Sponsored by Microsoft" className="h-5 hidden md:block" />
              <span className="text-levelup-accent font-bold text-base hidden lg:inline">|</span>
              <span className="text-white font-semibold text-sm hidden lg:inline">Skilling in a Box</span>
            </button>
          </div>

          {/* Right: LevelUp link + Profile dropdown */}
          {user && (
            <div className="flex items-center space-x-3">
              {/* LevelUp Home external link */}
              <a
                href="https://skillupwithlevelup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1.5 text-gray-300 hover:text-levelup-accent transition-colors text-sm border border-gray-600 hover:border-levelup-accent rounded-lg px-3 py-1.5"
                data-testid="navbar-levelup-link"
              >
                <ExternalLink size={14} />
                <span>LevelUp</span>
              </a>

              {/* Profile dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowProfile(p => !p)}
                  className="flex items-center gap-2 hover:opacity-90 transition-opacity"
                  data-testid="profile-dropdown-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-levelup-accent text-levelup-dark font-bold text-sm flex items-center justify-center flex-shrink-0">
                    {initials}
                  </div>
                  <span className="hidden md:inline text-white text-sm font-medium max-w-[120px] truncate">
                    {user.full_name || user.email}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-gray-300 transition-transform duration-200 ${showProfile ? 'rotate-180' : ''}`}
                  />
                </button>

                {showProfile && (
                  <div
                    className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 w-72 overflow-hidden"
                    data-testid="profile-dropdown"
                  >
                    {/* User info header */}
                    <div className="bg-levelup-dark px-4 py-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-levelup-accent text-levelup-dark font-bold text-base flex items-center justify-center flex-shrink-0">
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">
                          {user.full_name || user.email}
                        </p>
                        <p className="text-gray-400 text-xs">{roleLabel} · {user.domain}</p>
                      </div>
                    </div>

                    {/* Language selector */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe size={14} className="text-gray-400" />
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Language</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {languageOptions.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => { i18n.changeLanguage(lang.code); }}
                            className={`text-left text-xs px-2 py-1.5 rounded-md transition-colors ${
                              i18n.language === lang.code
                                ? 'bg-levelup-accent text-levelup-dark font-semibold'
                                : 'text-gray-600 hover:bg-gray-50'
                            }`}
                            data-testid={`lang-option-${lang.code}`}
                          >
                            {lang.nativeName}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-4 py-2">
                      <button
                        onClick={() => { setShowProfile(false); onLogout(); }}
                        className="w-full flex items-center gap-3 px-2 py-2.5 text-sm text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid="profile-logout-btn"
                      >
                        <LogOut size={16} />
                        {t('common.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
